import React, { Children, cloneElement } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
  DotGroup,
} from 'pure-react-carousel';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'components/Icon';
import style from './Carousel.module.scss';

const getSlideCount = (width, type = 'tile') => {
  if (width >= 0 && width < 768) {
    switch (type) {
      case 'tile':
        return 2;
      case 'tile-series':
        return 3;
      case 'poster':
      case 'poster-series':
        return 3;
      default:
        return 1;
    }
  }
  if (width >= 768 && width < 1024) {
    switch (type) {
      case 'tile':
        return 2;
      case 'tile-series':
        return 3;
      case 'poster':
      case 'poster-series':
        return 3;
      default:
        return 1;
    }
  }
  if (width >= 1024 && width < 1440) {
    switch (type) {
      case 'tile':
        return 3;
      case 'tile-series':
        return 4;
      case 'poster':
      case 'poster-series':
        return 4;
      default:
        return 3;
    }
  }
  if (width >= 1440 && width < 2560) {
    switch (type) {
      case 'tile':
        return 4;
      case 'tile-series':
        return 5;
      case 'poster':
      case 'poster-series':
        return 5;
      default:
        return 4;
    }
  }
  if (width >= 2560) {
    switch (type) {
      case 'tile':
      case 'tile-series':
        return 5;
      case 'poster':
      case 'poster-series':
        return 6;
      default:
        return 5;
    }
  }
  return 0;
};

const Carousel = ({ children, type }) => {
  const { width } = useWindowSize();
  if (!children) return null;
  const arrayChildren = Children.toArray(children);
  const { showcaseCarouselAutoscrollSeconds } = window.ExternalConfig;
  let typeProps = {
    infinite: true,
    interval: showcaseCarouselAutoscrollSeconds,
    isPlaying: true,
    visibleSlides: 1,
    step: 1,
  };
  let dots = false;
  const prevButtonStyle = [style.navButton, style.prev];
  const nextButtonStyle = [style.navButton, style.next];
  const carouselStyle = [style.carousel];
  const dotGroupStyle = [style.dotGroup];
  const slides = getSlideCount(width, type);
  if (type !== 'showcase') {
    typeProps = {
      infinite: false,
      isPlaying: false,
      visibleSlides: slides,
      step: slides,
    };
    carouselStyle.push(style.itemSlider);
  }
  switch (type) {
    case 'showcase':
      dots = true;
      break;
    case 'tile':
      prevButtonStyle.push(style.tilePrev);
      nextButtonStyle.push(style.tileNext);
      break;
    case 'tile-series':
      prevButtonStyle.push(style.tileSeriesPrev);
      nextButtonStyle.push(style.tileSeriesNext);
      break;
    case 'poster':
    case 'poster-series':
      prevButtonStyle.push(style.posterPrev);
      nextButtonStyle.push(style.posterNext);
      break;
    default:
      break;
  }
  if (type === 'showcase' && arrayChildren?.length <= 1) return children;
  return (
    <CarouselProvider
      isIntrinsicHeight
      totalSlides={arrayChildren?.length}
      className={classNames(carouselStyle)}
      touchEnabled
      aria-hidden="true"
      {...typeProps}
    >
      <Slider
        moveThreshold={0.05}
        classNameAnimation={style.slideAnimation}
        aria-live="off"
        role={undefined}
        trayProps={{ role: 'listbox' }}
        aria-hidden="true"
      >
        {Children.map(arrayChildren, (child, idx) => (
          <Slide
            classNameHidden={style.hide}
            classNameVisible={style.show}
            index={idx}
            tabIndex={0}
            aria-selected={false}
            aria-live="off"
            aria-hidden="true"
            aria-label=""
          >
            {cloneElement(child)}
          </Slide>
        ))}
      </Slider>
      <ButtonBack className={classNames(prevButtonStyle)}>
        <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-left" ariaHidden />
      </ButtonBack>
      <ButtonNext className={classNames(nextButtonStyle)}>
        <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-right" ariaHidden />
      </ButtonNext>
      {dots && <DotGroup className={classNames(dotGroupStyle)} />}
    </CarouselProvider>
  );
};

Carousel.defaultProps = {};

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  type: PropTypes.string.isRequired,
};

export default Carousel;
